import type { FC, FocusEvent } from 'react'
import { ChangeEvent, useEffect, useRef } from 'react'
import { FastField, FastFieldProps, FieldInputProps } from 'formik'
import { TextField, TextFieldProps } from '@mui/material'
import { assertNever } from '@dhl/utils'
import { useFocusOnFirstError } from '@dhl/use-focus-on-first-error'
import { TextFieldFormat, FieldName, TextTransform } from '@dhl/receiver-address-format-api'
import { AddressLabel } from './AddressLabel.js'

const getAutoCompleteByFieldName = (fieldName: FieldName): string => {
  switch (fieldName) {
    case 'PostalCode':
      return 'postal-code'
    case 'HouseNumber':
      return 'address-line2'
    case 'HouseNumberSuffix':
      return 'nope'
    case 'Street':
      return 'street'
    case 'City':
      return 'city'
    case 'Suburb':
      return 'nope'
    case 'AdditionalAddressLine':
      return 'nope'
    default:
      return assertNever(fieldName)
  }
}

export type AddressTextFieldInputProps = Omit<
  TextFieldProps,
  | keyof FieldInputProps<string>
  | 'error'
  | 'inputRef'
  | 'label'
  | 'helperText'
  | 'required'
  | 'autoComplete'
  | 'inputProps'
> & {
  name: string
  fieldName: FieldName
  fieldFormat: TextFieldFormat
  isAutoCompleteEnabled?: boolean
  useManualAutoFocus?: boolean
}

export const AddressTextFieldInput: FC<AddressTextFieldInputProps> = ({
  name,
  fieldName,
  fieldFormat,
  isAutoCompleteEnabled = false,
  variant = 'outlined',
  useManualAutoFocus = false,
  ...props
}) => {
  const inputRef = useRef<HTMLInputElement>(null)

  useFocusOnFirstError(name, inputRef, inputRef)

  // Sometimes (example when used in a Dialog) the native autoFocus doesn't work in each browser/device
  // Therefore we use a simple 'hack' to set the focus on the field with a minimum of timeout.
  useEffect(() => {
    if (useManualAutoFocus) {
      setTimeout(() => {
        inputRef.current?.focus()
      }, 100)
    }
  }, [inputRef, useManualAutoFocus])

  return (
    <FastField name={name}>
      {({ field, meta, form }: FastFieldProps) => (
        <TextField
          {...props}
          {...field}
          data-test={name}
          variant={variant}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            const transform = (value: string, textTransform: TextTransform) => {
              switch (textTransform) {
                case 'Uppercase':
                  return value.toUpperCase()
                case 'NoWhitespace':
                  return value.replace(/\s/g, '')
                case 'OnlyDigits':
                  return value.replace(/[^0-9]/g, '')
                default:
                  return assertNever(textTransform)
              }
            }

            return field.onChange(name)(
              fieldFormat.textTransforms ? fieldFormat.textTransforms.reduce(transform, e.target.value) : e.target.value
            )
          }}
          onBlur={(event: FocusEvent<HTMLInputElement>) => {
            // Update the touched flag, but don't trigger a validation. If needed, we will trigger a validation
            // on the `setValue`. We don't want to trigger two validations. This is also the reason
            // why we don't call `field.onBlur` here.
            form.setFieldTouched(name, true, false)

            // Update the value with the trimmed input and trigger a validation if validateOnBlur equals true
            form.setFieldValue(name, event.target.value.trim(), form.validateOnBlur)
          }}
          inputRef={inputRef}
          label={<AddressLabel fieldName={fieldName} />}
          error={!!meta.error && meta.touched}
          helperText={meta.touched && meta.error}
          required={fieldFormat.isRequired}
          autoComplete={isAutoCompleteEnabled ? getAutoCompleteByFieldName(fieldName) : 'off'}
          slotProps={{
            htmlInput: {
              inputMode: fieldFormat.textTransforms?.includes('OnlyDigits') ? 'numeric' : undefined,
            },
          }}
        />
      )}
    </FastField>
  )
}
